import { z } from 'zod';
import { AgentRunStatusSchema } from './enums';
import { NonEmptyStringSchema, TimestampSchema } from './primitives';

/**
 * What an assessment is waiting on — the one thing the console reads to
 * decide what to show (docs/DECISIONS.md ZD-121 §5.6, ZD-123 §4, ZD-124).
 *
 * `GET /v1/assessments/:id` carries this as `next`, derived in the Durable
 * Object from the transition table, the pending alarm and the last recorded
 * run, and stored nowhere. It is the whole protocol between the driver and a
 * screen: a console that needs something this does not say has found a
 * defect in this schema, not a reason to read the state name.
 *
 * ## Why it is a schema here and not a type in the driver
 *
 * The driver computes it and the console parses it. A TypeScript type on one
 * side and a structural read on the other would be two expressions of one
 * shape that nothing compares (ZD-017), and the day one gained a field the
 * other would go on parsing the old one. So the shape is written once, the
 * driver's return type is its inference, and the browser parses with the
 * same object.
 *
 * ## The four kinds
 *
 * - `running` — a step is scheduled or executing. `stage` is the stage that
 *   will run or is running, so a page can say *what* rather than only *that*.
 * - `person` — nothing runs from here. `events` is every edge out of the
 *   state; none of them is a stage's, which is what makes them a person's.
 *   The review gate is this kind with `approver_decided` among the events.
 * - `ready` — a stage can run, nothing is running, and no run says otherwise.
 * - `stopped` — a stage can run, nothing is running, and the last recorded
 *   run did not advance: `error` or `rejected`, with its reason, verbatim.
 *
 * ## `events` on `ready` and `stopped` as well as `person`
 *
 * A person may still act at a state a stage runs from — add a document
 * before pressing, most obviously — and a page that offered that without
 * being told would be assuming `artefact_uploaded`'s edge out of every state
 * but `closed` (ZD-069), which is the table in a bundle by another route. So
 * the three kinds a person can act at all carry the person's events: every
 * edge out of the state that is not the stage's own. `running` carries none,
 * because while a step is in flight the object is the driver's and a page
 * that changed its inputs would be standing in ZD-121 §5's race.
 *
 * ## `stage.label`
 *
 * `worker_name` is the record's — `canonical_model_service`,
 * `automated_qa_review` — and a person waiting through a run is not reading
 * the record. `label` is the sentence the stage declares for a client, one
 * per stage, beside the worker name it labels (the audience rule: a field's
 * audience is a property of the field).
 */

export const NextStageSchema = z.object({
  /** The transition the stage records; an event name and never a state. */
  event: NonEmptyStringSchema,
  /** What the run is recorded as. Internal, carried for the record and not for a screen. */
  worker_name: NonEmptyStringSchema,
  /** What a client is told is happening. */
  label: NonEmptyStringSchema,
});
export type NextStage = z.infer<typeof NextStageSchema>;

export const NextSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('running'),
    stage: NextStageSchema,
    alarm_at: TimestampSchema.nullable(),
    step_started_at: TimestampSchema.nullable(),
  }),
  z.object({
    kind: z.literal('person'),
    events: z.array(NonEmptyStringSchema),
  }),
  z.object({
    kind: z.literal('ready'),
    stage: NextStageSchema,
    events: z.array(NonEmptyStringSchema),
  }),
  z.object({
    kind: z.literal('stopped'),
    stage: NextStageSchema,
    events: z.array(NonEmptyStringSchema),
    run: z.object({
      run_id: NonEmptyStringSchema,
      worker_name: NonEmptyStringSchema,
      status: AgentRunStatusSchema,
      /** The run's own sentence — a guard's reason or an error's message — never composed. */
      reason: z.string(),
    }),
  }),
]);
export type Next = z.infer<typeof NextSchema>;

/**
 * Why a transition was refused — the other half of the protocol between the
 * orchestrator and a screen (docs/DECISIONS.md ZD-125 §3).
 *
 * `evaluateTransition` produces this and a route answers it as a 409; the
 * console at the gate parses it. Here for `NextSchema`'s reason: a type in the
 * API beside a structural read in a browser is two expressions of one shape.
 *
 * ## `finding_ids` beside the sentence, and why the sentence loses them
 *
 * The gate's guard refused with sentences ending in a list of finding ids —
 * *"5 findings are deferred pending answers and cannot be reported either
 * way: finding_a1…, finding_b2…"* — which an auditor can act on and a person
 * at a screen cannot: a finding id names nothing a reviewer recognises, and
 * `sds-12c` measured it on the first live deferral. So the ids travel as a
 * field, the sentence is the sentence, and a screen names the findings by
 * title through what it already holds. The audit event and the run record
 * carry both. A guard whose refusal is about no finding in particular carries
 * an empty list, which is a declaration and not an omission (ZD-035 §2).
 */
export const TransitionRejectionSchema = z.discriminatedUnion('code', [
  z.object({
    code: z.literal('unknown_transition'),
    reason: NonEmptyStringSchema,
  }),
  z.object({
    code: z.literal('guard_failed'),
    reason: NonEmptyStringSchema,
    /** The guard as the transition table words it, for the audit record. */
    guardDescription: NonEmptyStringSchema,
    /** The findings the refusal is about, where it is about findings. */
    finding_ids: z.array(NonEmptyStringSchema),
  }),
]);
export type TransitionRejection = z.infer<typeof TransitionRejectionSchema>;
